import React, { useState, useRef } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Table, Spinner } from "reactstrap";
import Swal from "sweetalert2";
import axios from "axios";
import * as XLSX from "xlsx";

// Alta de pedidos desde Excel con los mismos datos de la pantalla ArticulosCreate.
// Un renglon = modelo + color, una columna por talla. Si algo no coincide no se guarda nada.
function LayoutPedidos({ disabled }) {
  const URL_PEDIDOS = process.env.REACT_APP_URL_PEDIDOS;
  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };
  const inputFile = useRef(null);

  const [modal, setModal] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [archivo, setArchivo] = useState("");
  const [rows, setRows] = useState([]);
  const [resultado, setResultado] = useState(null);

  const toggle = () => {
    if (cargando) return;
    setModal(!modal);
  };

  const errorAxios = (err) =>
    Swal.fire("Error", (err.response && err.response.data && (err.response.data.error || err.response.data.message)) || err.message, "error");

  // Layout de ejemplo: hoja Pedidos (encabezados + renglon de ejemplo), Catalogos e Instrucciones
  function descargarLayout() {
    setCargando(true);
    axios
      .get(`${URL_PEDIDOS}LayoutPlantilla`, { headers })
      .then(({ data }) => {
        const { columnas, tallas, catalogos } = data;
        const primero = (k) => (catalogos[k] && catalogos[k][0]) || "";
        const encabezados = [...columnas, ...tallas];
        const ejemplo = {
          "CLIENTE*": primero("CLIENTES"),
          "FECHA VENTA": new Date().toISOString().slice(0, 10),
          "MODELO PROVISIONAL*": "EJEMPLO-BORRAR",
          "DEPARTAMENTO*": primero("DEPARTAMENTOS"),
          "CLASE*": primero("CLASES"),
          "ETIQUETA*": primero("ETIQUETAS"),
          DESCRIPCION: "VESTIDO LARGO",
          "COLECCION*": primero("COLECCIONES"),
          "VENDEDOR*": primero("VENDEDORES"),
          "COMPRADOR*": primero("COMPRADORES"),
          "PEDIDO CLIENTE*": "12345",
          "MODELO OFICIAL": "NA",
          "OBSERVACIONES DISEÑO": "NA",
          PROCESOS: primero("PROCESOS"),
          "EMPRESA*": primero("EMPRESAS"),
          "FECHA CANCELACION*": "2027-01-31",
          "FECHA CONFIRMACION": "2026-12-21",
          "PENDIENTE FECHA CONF*": "No",
          "PRECIO VENTA*": 250,
          "PRECIO ETIQUETA": 499,
          "COLOR*": primero("COLORES"),
        };
        tallas.slice(0, 4).forEach((t, i) => (ejemplo[t] = [10, 20, 20, 10][i]));
        const fila = encabezados.map((h) => (ejemplo[h] === undefined ? "" : ejemplo[h]));

        const wsPedidos = XLSX.utils.aoa_to_sheet([encabezados, fila]);
        wsPedidos["!cols"] = encabezados.map((h) => ({ wch: Math.max(8, h.length + 2) }));

        const listas = { ...catalogos, TALLAS: tallas };
        const nombresCat = Object.keys(listas);
        const largo = Math.max(...nombresCat.map((k) => listas[k].length), 0);
        const aoaCat = [nombresCat];
        for (let i = 0; i < largo; i++) aoaCat.push(nombresCat.map((k) => listas[k][i] || ""));
        const wsCat = XLSX.utils.aoa_to_sheet(aoaCat);
        wsCat["!cols"] = aoaCat[0].map(() => ({ wch: 28 }));

        const wsInst = XLSX.utils.aoa_to_sheet([
          ["INSTRUCCIONES"],
          ["Son los mismos datos de la pantalla Nuevo Pedido (ArticulosCreate)."],
          ["Un renglon por MODELO + COLOR. En las columnas de talla se pone la cantidad (vacio = 0)."],
          ["Los renglones con el mismo MODELO PROVISIONAL y PEDIDO CLIENTE forman un solo pedido."],
          ["Si el MODELO PROVISIONAL ya existe, se crea solo el pedido sobre ese articulo (resurtido)."],
          ["Las columnas con * son obligatorias. Los nombres deben ser iguales a la hoja Catalogos."],
          ["PROCESOS: varios separados por coma. PENDIENTE FECHA CONF: Si o No."],
          ["FECHA CONFIRMACION vacia = FECHA CANCELACION menos 41 dias (igual que la pantalla). FECHA VENTA vacia = hoy."],
          ["Fechas en formato AAAA-MM-DD o DD/MM/AAAA."],
          ["El renglon EJEMPLO-BORRAR se ignora; puede borrarlo."],
          ["Si algo no coincide con los catalogos NO se guarda nada: corrija el archivo y vuelva a subirlo."],
          ["El consumo de telas se captura despues en el pedido."],
        ]);
        wsInst["!cols"] = [{ wch: 110 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsPedidos, "Pedidos");
        XLSX.utils.book_append_sheet(wb, wsCat, "Catalogos");
        XLSX.utils.book_append_sheet(wb, wsInst, "Instrucciones");
        XLSX.writeFile(wb, "LayoutPedidos.xlsx");
      })
      .catch(errorAxios)
      .finally(() => setCargando(false));
  }

  // Lee el Excel y pide la vista previa al back (no guarda nada)
  function leerArchivo(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      let datos;
      try {
        const wb = XLSX.read(new Uint8Array(ev.target.result), { type: "array" });
        const hoja = wb.Sheets["Pedidos"] || wb.Sheets[wb.SheetNames[0]];
        datos = XLSX.utils.sheet_to_json(hoja, { defval: "", raw: true }).map((r) => ({ ...r, __fila: r.__rowNum__ + 1 }));
      } catch (err) {
        Swal.fire("Error", "No se pudo leer el archivo de Excel", "error");
        return;
      }
      if (!datos.length) {
        Swal.fire("Archivo vacio", "La hoja Pedidos no trae renglones", "warning");
        return;
      }
      setArchivo(file.name);
      setRows(datos);
      setResultado(null);
      setModal(true);
      setCargando(true);
      axios
        .post(`${URL_PEDIDOS}Layout`, { rows: datos, guardar: false }, { headers })
        .then(({ data }) => setResultado(data))
        .catch(errorAxios)
        .finally(() => setCargando(false));
    };
    reader.readAsArrayBuffer(file);
  }

  const grupos = (resultado && resultado.grupos) || [];
  const generales = (resultado && resultado.generales) || [];
  const conError = grupos.filter((g) => g.estado === "error").length;
  const conAviso = grupos.filter((g) => g.avisos && g.avisos.length).length;
  const puedeGuardar = resultado && !cargando && grupos.length > 0 && conError === 0 && generales.length === 0;
  const totPiezas = grupos.reduce((t, g) => t + (g.totalPiezas || 0), 0);
  const totImporte = grupos.reduce((t, g) => t + (g.totalGeneral || 0), 0);
  const fmt = (n) => new Intl.NumberFormat("en-US").format(n);

  async function guardar() {
    if (!puedeGuardar) return;
    // Si hay avisos (algo que no coincide del todo) se pregunta antes de guardar
    const confirma = await Swal.fire({
      title: conAviso ? "Hay avisos en el archivo" : "Guardar pedidos",
      html: conAviso
        ? `${conAviso} pedido(s) tienen avisos (marcados en amarillo). Reviselos antes de continuar.<br/><br/>¿Guardar los ${grupos.length} pedidos de todos modos?`
        : `Se crearan ${grupos.length} pedido(s) con ${fmt(totPiezas)} piezas por $${fmt(totImporte.toFixed(2))}`,
      icon: conAviso ? "warning" : "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, guardar",
      cancelButtonText: "Cancelar",
    });
    if (!confirma.value) return;
    setCargando(true);
    axios
      .post(`${URL_PEDIDOS}Layout`, { rows, guardar: true }, { headers })
      .then(({ data }) => {
        setResultado(data);
        if (!data.guardado) {
          Swal.fire("No se guardo nada", data.mensaje || "El archivo tiene errores", "error");
          return;
        }
        const folios = data.grupos.map((g) => `#${g.idPedido}`).join(", ");
        Swal.fire({
          title: "Pedidos creados",
          html: `Se crearon ${data.grupos.length} pedido(s): ${folios}`,
          icon: "success",
        }).then(() => window.location.reload());
      })
      .catch(errorAxios)
      .finally(() => setCargando(false));
  }

  const colorEstado = (g) => (g.estado === "error" ? "#f8d7da" : g.avisos && g.avisos.length ? "#fff3cd" : "");
  const textoEstado = (g) =>
    g.estado === "error" ? "Error" : g.estado === "existente" ? "Pedido sobre modelo existente" : "Modelo nuevo + pedido";

  return (
    <>
      <Button size="sm" className="btn btn-info" onClick={descargarLayout} disabled={disabled || cargando}>
        Layout Ejemplo <i class="far fa-file-excel"></i>
      </Button>
      <Button size="sm" className="btn btn-primary" onClick={() => inputFile.current.click()} disabled={disabled || cargando}>
        Subir Layout <i class="fas fa-upload"></i>
      </Button>
      <input type="file" accept=".xlsx,.xls" ref={inputFile} style={{ display: "none" }} onChange={leerArchivo} />

      <Modal size="xl" isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>
          <h4>Alta de pedidos por layout {archivo ? `- ${archivo}` : ""}</h4>
        </ModalHeader>
        <ModalBody>
          {cargando && !resultado ? (
            <div align="center">
              <Spinner color="primary" /> Revisando archivo...
            </div>
          ) : null}
          {resultado ? (
            <>
              {generales.length || conError ? (
                <div className="alert alert-danger">
                  <b>No se puede guardar: el archivo no coincide al 100%. No se guardara nada hasta corregirlo.</b>
                  {generales.length ? (
                    <ul className="mb-0">
                      {generales.map((m, i) => (
                        <li key={i}>{m}</li>
                      ))}
                    </ul>
                  ) : null}
                  {conError ? <div>{conError} pedido(s) con error (en rojo).</div> : null}
                </div>
              ) : resultado.guardado ? (
                <div className="alert alert-success">Pedidos guardados.</div>
              ) : (
                <div className={conAviso ? "alert alert-warning" : "alert alert-success"}>
                  Todo coincide con los catalogos. {grupos.length} pedido(s), {fmt(totPiezas)} piezas, ${fmt(totImporte.toFixed(2))}.
                  {conAviso ? ` ${conAviso} pedido(s) tienen avisos (en amarillo): reviselos antes de guardar.` : ""}
                </div>
              )}
              <Table size="sm" striped borderless className="table-responsive-xl">
                <thead>
                  <tr>
                    <th>Renglones</th>
                    <th>Estado</th>
                    <th>Modelo</th>
                    <th>Descripcion</th>
                    <th>Cliente</th>
                    <th>Pedido Cliente</th>
                    <th>Coleccion</th>
                    <th>Colores</th>
                    <th>Tallas</th>
                    <th>Piezas</th>
                    <th>Total</th>
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {grupos.map((g) => (
                    <tr key={g.llave} style={{ backgroundColor: colorEstado(g) }}>
                      <td>{g.filas.join(", ")}</td>
                      <td>
                        {textoEstado(g)}
                        {g.idPedido ? ` #${g.idPedido}` : ""}
                      </td>
                      <td>{g.codigo}</td>
                      <td>{g.descripcion}</td>
                      <td>{g.cliente}</td>
                      <td>{g.pedidoCliente}</td>
                      <td>{g.temporada}</td>
                      <td>{g.colores.join(", ")}</td>
                      <td>{g.tallas.join(", ")}</td>
                      <td align="right">{fmt(g.totalPiezas)}</td>
                      <td align="right">${fmt(g.totalGeneral)}</td>
                      <td>
                        {g.errores.map((m, i) => (
                          <div key={"e" + i} style={{ color: "#a71d2a" }}>
                            {m}
                          </div>
                        ))}
                        {g.avisos.map((m, i) => (
                          <div key={"a" + i} style={{ color: "#856404" }}>
                            {m}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          {resultado && !resultado.guardado ? (
            <Button color="success" onClick={guardar} disabled={!puedeGuardar}>
              {cargando ? <Spinner size="sm" /> : null} Guardar {grupos.length} pedido(s)
            </Button>
          ) : null}
          <Button color="danger" onClick={toggle} disabled={cargando}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default LayoutPedidos;
